import { Link } from "react-router-dom";
import { Check, Tag, ArrowRight } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";

const COUPON = { code: "LAUNCH20", text: "20% off your first engagement", until: "November 7, 2026" };

const tiers = [
  {
    name: "Free Exposure Check",
    price: "$0",
    cadence: "instant · self-serve",
    blurb: "A public-information check of your email security and website encryption — the conversation starter.",
    features: [
      "SPF, DKIM & DMARC email-spoofing check",
      "Website HTTPS/TLS certificate check",
      "Instant results + emailed one-page summary",
      "Public information only — no scans, no account",
    ],
    cta: "Run the free check",
    to: "/exposure-check",
    featured: false,
  },
  {
    name: "Baseline Review",
    price: "$499",
    cadence: "one-time",
    blurb: "A fast, fixed-scope cybersecurity baseline for small teams — know exactly where you stand.",
    features: [
      "Guided security posture questionnaire",
      "External attack-surface scan",
      "Top-10 risk findings, plain English",
      "30-min readout call with a consultant",
    ],
    cta: "Book baseline review",
    to: "/baseline-review",
    featured: false,
    badge: "Entry offer",
  },
  {
    name: "HIPAA Security Risk Assessment",
    price: "$2,500 – $4,500",
    cadence: "one-time · $2,500 under 15 staff",
    blurb: "The written risk analysis HIPAA requires — built for independent medical and dental practices.",
    features: [
      "Written security risk analysis for your records",
      "Prioritized remediation plan",
      "Policy & staff-training gaps identified",
      "Executive readout in plain English",
      "BAA signed with every client",
    ],
    cta: "See HIPAA details",
    to: "/hipaa",
    featured: true,
  },
  {
    name: "Compliance Readiness",
    price: "Starting at $12,000",
    cadence: "per engagement",
    blurb: "End-to-end SOC 2 / ISO 27001 readiness with policies, controls, and audit prep.",
    features: [
      "Gap analysis against your target framework",
      "Policy & control implementation",
      "Evidence collection & audit prep",
      "Auditor coordination support",
      "Dedicated engagement lead",
    ],
    cta: "Start readiness program",
    featured: false,
  },
];

const retainers = [
  {
    name: "Care Essential",
    price: "$750",
    cadence: "/ month",
    blurb: "Keep your risk analysis current and your questions answered — the minimum every practice needs.",
    features: [
      "Quarterly security checks",
      "Policy updates as rules change",
      "Email access to your consultant",
      "Annual baseline review refresh",
    ],
    cta: "Start Essential",
    featured: false,
  },
  {
    name: "Care Plus",
    price: "$1,150",
    cadence: "/ month",
    blurb: "Active compliance upkeep with staff training and insurance questionnaire support.",
    features: [
      "Everything in Care Essential",
      "Staff security awareness training",
      "Cyber-insurance questionnaire support",
      "Quarterly posture review & roadmap update",
    ],
    cta: "Start Plus",
    featured: true,
  },
  {
    name: "Care Complete",
    price: "$1,500",
    cadence: "/ month",
    blurb: "Full compliance care with an incident hotline and annual assessment refresh included.",
    features: [
      "Everything in Care Plus",
      "Incident hotline with priority response",
      "Annual full risk assessment refresh",
      "Vendor security reviews",
    ],
    cta: "Start Complete",
    featured: false,
  },
];

export default function Pricing() {
  return (
    <PageLayout
      seoTitle="Pricing & Packages | Aliwer Cybersecurity"
      seoDescription="Transparent pricing: free exposure check, $499 baseline review, HIPAA security risk assessments $2,500–$4,500, SOC 2 / ISO readiness, and Compliance Care plans $750–$1,500/month."
      path="/pricing"
      eyebrow="Pricing"
      title="Simple packages, scoped to you."
      subtitle="Indicative starting prices below. Final scope and pricing are confirmed in a free consult — every engagement is tailored to your size, stack, and target framework."
      testid="pricing-page"
    >
      {/* Launch coupon promo */}
      <div
        className="mb-12 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-paper/20 bg-surface p-5"
        data-testid="pricing-coupon"
      >
        <span className="inline-flex items-center gap-2 text-paper">
          <Tag size={16} />
          <span className="font-display text-lg tracking-tight">Launch offer</span>
        </span>
        <p className="text-sm text-fog">
          Use code <span className="text-paper font-medium tracking-wide">{COUPON.code}</span> for {COUPON.text} —
          through {COUPON.until}.
        </p>
      </div>

      <p className="text-xs uppercase tracking-[0.3em] text-fog mb-6">One-time engagements</p>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {tiers.map((t) => (
          <Tier key={t.name} t={t} />
        ))}
      </div>

      <div className="mt-16" data-testid="pricing-retainers">
        <p className="text-xs uppercase tracking-[0.3em] text-fog mb-2">Compliance Care — monthly plans</p>
        <p className="text-sm text-fog mb-6 max-w-2xl">
          Ongoing plans that keep your risk analysis, policies, and staff training current after the first
          engagement — from $750 to $1,500 per month.
        </p>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {retainers.map((t) => (
            <Tier key={t.name} t={t} />
          ))}
        </div>
      </div>

      <p className="mt-10 text-xs text-fog/70 leading-relaxed max-w-2xl">
        Prices are indicative starting points and vary with scope, environment complexity, and timeline. Nonprofits and
        early-stage startups may qualify for adjusted pricing — ask us in your consult.
      </p>
    </PageLayout>
  );
}

function Tier({ t }) {
  return (
    <div
      data-testid={`pricing-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
      className={`flex flex-col rounded-2xl border p-7 transition-colors ${
        t.featured ? "border-paper/40 bg-surface" : "border-white/10 bg-black/40 hover:border-white/25"
      }`}
    >
      {t.featured && (
        <span className="self-start mb-4 text-[10px] uppercase tracking-widest bg-paper text-black rounded-full px-2.5 py-1">
          Most popular
        </span>
      )}
      {t.badge && (
        <span className="self-start mb-4 text-[10px] uppercase tracking-widest border border-white/25 text-paper rounded-full px-2.5 py-1">
          {t.badge}
        </span>
      )}
      <h2 className="font-display text-xl tracking-tight text-paper">{t.name}</h2>
      <div className="mt-4">
        <span className="font-display text-3xl tracking-tighter text-paper">{t.price}</span>
        <span className="ml-2 text-xs text-fog">{t.cadence}</span>
      </div>
      <p className="mt-4 text-sm text-fog leading-relaxed">{t.blurb}</p>
      <ul className="mt-6 space-y-2.5 flex-1">
        {t.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-paper/90">
            <Check size={16} className="text-fog mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to={t.to || "/contact"}
        data-testid={`pricing-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
        className={`group mt-7 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors ${
          t.featured ? "bg-paper text-black hover:bg-white" : "border border-white/20 text-paper hover:bg-white/5"
        }`}
      >
        {t.cta}
        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
}
